import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import ADD_ORDER from 'api/mutations/ADD_ORDER';
import DUPLICATE_ORDER from 'api/mutations/DUPLICATE_ORDER';
import UPDATE_ORDER_APPROVE from 'api/mutations/UPDATE_ORDER_APPROVE';
import UPDATE_ORDER_CANCEL from 'api/mutations/UPDATE_ORDER_CANCEL';
import UPDATE_ORDER_COMPLETE from 'api/mutations/UPDATE_ORDER_COMPLETE';
import REMOVE_ORDER from 'api/mutations/REMOVE_ORDER';
import CREATE_ORDER_CORRECTION from 'api/mutations/CREATE_ORDER_CORRECTION';
import GET_ORDER from 'api/queries/GET_ORDER';
import { toast } from 'components/common/Toast/Toast';
import { useHistory } from 'react-router';
export default function useOrders() {
    const { t } = useTranslation(['orders']);
    const history = useHistory();
    const [addOrderMutation] = useMutation(ADD_ORDER, { refetchQueries: ['GetOrders'] });
    const [duplicateOrderMutation] = useMutation(DUPLICATE_ORDER, { refetchQueries: ['GetOrders'] });
    const [updateOrderApproveMutation] = useMutation(UPDATE_ORDER_APPROVE, { refetchQueries: ['GetOrders'] });
    const [updateOrderCancelMutation] = useMutation(UPDATE_ORDER_CANCEL, { refetchQueries: ['GetOrders'] });
    const [updateOrderCompleteMutation] = useMutation(UPDATE_ORDER_COMPLETE, { refetchQueries: ['GetOrders'] });
    const [removeOrderMutation] = useMutation(REMOVE_ORDER, { refetchQueries: ['GetOrders'] });
    const [createOrderCorrectionMutation] = useMutation(CREATE_ORDER_CORRECTION, { refetchQueries: ['GetOrders'] });
    const addOrder = useCallback(() => {
        return addOrderMutation().then((response) => {
            var _a, _b, _c;
            const orderId = (_c = (_b = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.addOrder) === null || _b === void 0 ? void 0 : _b.order) === null || _c === void 0 ? void 0 : _c.id;
            history.replace(`/orders/${orderId}/specifications`);
            toast('success', t('order_added'));
        });
    }, []);
    const removeOrder = useCallback((orderNodeId) => {
        return removeOrderMutation({ variables: { orderNodeId } }).then(() => {
            toast('success', t('order_removed'));
        });
    }, []);
    const duplicateOrder = useCallback((orderNodeId) => {
        return duplicateOrderMutation({ variables: { orderNodeId } }).then(() => {
            toast('success', t('order_duplicated'));
        });
    }, []);
    const createOrderCorrection = useCallback((orderNodeId) => {
        return createOrderCorrectionMutation({ variables: { orderNodeId } }).then(response => {
            var _a, _b, _c;
            toast('success', t('correction_created'));
            return (_c = (_b = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.createOrderCorrection) === null || _b === void 0 ? void 0 : _b.order) === null || _c === void 0 ? void 0 : _c.id;
        });
    }, []);
    const updateOrderApprove = useCallback((orderNodeId) => {
        return updateOrderApproveMutation({ variables: { orderNodeId }, refetchQueries: [
                { query: GET_ORDER, variables: { id: orderNodeId } },
            ] }).then(() => {
            toast('success', t('order_approved'));
        });
    }, []);
    const updateOrderCancel = useCallback((orderNodeId) => {
        return updateOrderCancelMutation({ variables: { orderNodeId }, refetchQueries: [
                { query: GET_ORDER, variables: { id: orderNodeId } },
            ] }).then(() => {
            toast('success', t('order_cancelled'));
        });
    }, []);
    const updateOrderComplete = useCallback((orderNodeId) => {
        return updateOrderCompleteMutation({ variables: { orderNodeId }, refetchQueries: [
                { query: GET_ORDER, variables: { id: orderNodeId } },
            ] }).then(() => {
            toast('success', t('order_completed'));
        });
    }, []);
    return { addOrder, duplicateOrder, removeOrder, updateOrderApprove, updateOrderCancel, updateOrderComplete, createOrderCorrection };
}
